import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState } from 'react';
import { ClipboardDocumentIcon } from '@heroicons/react/24/outline';
import { ClipboardDocumentCheckIcon } from '@heroicons/react/24/solid';
import { MarkdownRenderer } from '@edusense/shared-ui-content';
import { Button } from '@edusense/shared-ui-kit';
import { Tooltip } from '@heroui/react';
import { motion, AnimatePresence } from 'framer-motion';
import { chat_message_assistant_generating, chat_message_actions_copied, chat_message_actions_copyMessage, chat_message_actions_copy } from '@paraglide/messages';
export default function ChatMessage({ content, role, timestamp, isLoading = false, onCopy, fontSize = 'medium', fontSizeValue }) {
    const [copied, setCopied] = useState(false);
    const isUser = role === 'user';
    const getFontSizeClass = ()=>{
        switch(fontSize){
            case 'small':
                return 'text-sm';
            case 'large':
                return 'text-lg';
            default:
                return 'text-base';
        }
    };
    const handleCopy = async ()=>{
        try {
            await navigator.clipboard.writeText(content);
            setCopied(true);
            onCopy?.();
            setTimeout(()=>setCopied(false), 2000);
        } catch (error) {
            console.error('Failed to copy:', error);
        }
    };
    return /*#__PURE__*/ _jsx("div", {
        className: "group",
        "data-testid": "ai-chat-message",
        "data-role": isUser ? 'user' : 'assistant',
        "data-loading": isLoading ? 'true' : 'false',
        children: /*#__PURE__*/ _jsx("div", {
            className: `flex-1 overflow-hidden ${isUser ? 'flex justify-end' : ''}`,
            children: /*#__PURE__*/ _jsxs("div", {
                className: isUser ? 'max-w-md' : 'w-full',
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        className: `${isUser ? 'rounded-lg border border-gray-200 bg-blue-50 p-3 dark:border-gray-700 dark:bg-blue-900/20' : ''}`,
                        children: isUser ? /*#__PURE__*/ _jsx("p", {
                            className: `whitespace-pre-wrap break-words text-gray-900 dark:text-gray-100 ${!fontSizeValue ? getFontSizeClass() : ''}`,
                            style: fontSizeValue ? {
                                fontSize: `${fontSizeValue}px`
                            } : undefined,
                            "data-testid": "ai-chat-message-content",
                            children: content
                        }) : /*#__PURE__*/ _jsx("div", {
                            "data-testid": "ai-chat-message-content",
                            children: isLoading ? /*#__PURE__*/ _jsxs("div", {
                                className: "flex items-center gap-2",
                                children: [
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "flex space-x-1",
                                        children: [
                                            /*#__PURE__*/ _jsx("div", {
                                                className: "h-2 w-2 animate-bounce rounded-full bg-current [animation-delay:-0.3s]"
                                            }),
                                            /*#__PURE__*/ _jsx("div", {
                                                className: "h-2 w-2 animate-bounce rounded-full bg-current [animation-delay:-0.15s]"
                                            }),
                                            /*#__PURE__*/ _jsx("div", {
                                                className: "h-2 w-2 animate-bounce rounded-full bg-current"
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx("span", {
                                        className: "text-sm text-gray-500 dark:text-gray-400",
                                        children: chat_message_assistant_generating()
                                    })
                                ]
                            }) : /*#__PURE__*/ _jsx(MarkdownRenderer, {
                                content: content,
                                enableCodeHighlight: true,
                                testId: "ai-chat-markdown-content",
                                fontSize: fontSize,
                                style: fontSizeValue ? {
                                    fontSize: `${fontSizeValue}px`
                                } : undefined
                            })
                        })
                    }),
                    !isLoading && /*#__PURE__*/ _jsx(motion.div, {
                        initial: {
                            opacity: 0,
                            y: 10
                        },
                        animate: {
                            opacity: 1,
                            y: 0
                        },
                        className: `mt-2 flex items-center gap-1 opacity-0 transition-all group-hover:opacity-100 ${isUser ? 'justify-end' : ''}`,
                        children: /*#__PURE__*/ _jsx(Tooltip, {
                            content: copied ? chat_message_actions_copied() : chat_message_actions_copyMessage(),
                            placement: "top",
                            delay: 0,
                            closeDelay: 0,
                            className: "px-2 py-1 text-xs",
                            children: /*#__PURE__*/ _jsx(Button, {
                                iconOnly: true,
                                icon: /*#__PURE__*/ _jsx(AnimatePresence, {
                                    mode: "wait",
                                    children: copied ? /*#__PURE__*/ _jsx(motion.div, {
                                        initial: {
                                            scale: 0
                                        },
                                        animate: {
                                            scale: 1
                                        },
                                        exit: {
                                            scale: 0
                                        },
                                        children: /*#__PURE__*/ _jsx(ClipboardDocumentCheckIcon, {
                                            className: "w-4 h-4 text-green-500"
                                        })
                                    }, "check") : /*#__PURE__*/ _jsx(motion.div, {
                                        initial: {
                                            scale: 0
                                        },
                                        animate: {
                                            scale: 1
                                        },
                                        exit: {
                                            scale: 0
                                        },
                                        children: /*#__PURE__*/ _jsx(ClipboardDocumentIcon, {
                                            className: "w-4 h-4"
                                        })
                                    }, "clipboard")
                                }),
                                variant: "ghost",
                                size: "sm",
                                onClick: handleCopy,
                                label: chat_message_actions_copy(),
                                className: "!w-7 !h-7 !min-w-0"
                            })
                        })
                    })
                ]
            })
        })
    });
}
